import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { t, login, loginWithGoogle } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      login(data.token, data.user);
      toast.success(mode === "login" ? t.auth.loginTitle : t.auth.registerTitle);
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 focus:border-tigris focus:ring-2 focus:ring-tigris/20 outline-none transition text-sm";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-tigris to-tigris-dark">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo.png?v=3" alt={t.brand} className="h-36 w-auto object-contain mb-3" data-testid="auth-logo-img" />
          <h1 className="text-2xl font-bold text-slate-800 font-display">{mode === "login" ? t.auth.loginTitle : t.auth.registerTitle}</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.auth.name}</label>
              <input data-testid="auth-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.auth.email}</label>
            <input data-testid="auth-email" type="email" dir="ltr" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.auth.password}</label>
            <input data-testid="auth-password" type="password" dir="ltr" className={inputCls} value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} />
          </div>
          <button type="submit" disabled={loading} data-testid="auth-submit"
            className="btn-primary w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : t.auth.submit}
          </button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">{t.auth.or}</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>
        <button type="button" data-testid="google-signin-btn" onClick={loginWithGoogle}
          className="w-full py-3 rounded-xl border-2 border-slate-200 hover:border-tigris hover:bg-slate-50 font-semibold text-sm text-slate-700 flex items-center justify-center gap-3 transition-colors">
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/>
            <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/>
            <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
          </svg>
          {t.auth.google}
        </button>
        <p className="text-center text-sm text-slate-500 mt-6">
          {mode === "login" ? t.auth.noAccount : t.auth.haveAccount}{" "}
          <button data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-tigris font-bold hover:underline">
            {mode === "login" ? t.auth.register : t.auth.login}
          </button>
        </p>
        <p className="text-center mt-3"><Link to="/" className="text-xs text-slate-400 hover:text-tigris">{t.brand}</Link></p>
      </div>
    </div>
  );
}
